import { useState, useEffect } from 'react';
import type { SpoolWithRelations, SpoolLocationType, MoveSpoolInput, TenantPrinterWithDetails } from '@maker-toolchest/shared';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment } from 'react';
import { X, MapPin } from 'lucide-react';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { usePrinters } from '@/features/printers';
import { useStorageLocations } from '@/features/storage';

interface MoveSpoolDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: MoveSpoolInput) => Promise<void>;
  spool: SpoolWithRelations | null;
  loading?: boolean;
}

const locationTypeOptions = [
  { value: 'NONE', label: 'No Location' },
  { value: 'STORAGE', label: 'Storage Location' },
  { value: 'PRINTER', label: 'Printer' },
];

export function MoveSpoolDialog({
  open,
  onClose,
  onConfirm,
  spool,
  loading = false,
}: MoveSpoolDialogProps) {
  const [locationType, setLocationType] = useState<SpoolLocationType>('NONE');
  const [printerId, setPrinterId] = useState<string>('');
  const [storageLocationId, setStorageLocationId] = useState<string>('');

  const { data: printersData } = usePrinters();
  const { data: storageData } = useStorageLocations();

  const printers = printersData || [];
  const storageLocations = storageData || [];

  useEffect(() => {
    if (open && spool) {
      setLocationType(spool.locationType);
      setPrinterId(spool.tenantPrinterId || '');
      setStorageLocationId(spool.storageLocationId || '');
    }
  }, [open, spool]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data: MoveSpoolInput = { locationType };
    if (locationType === 'PRINTER') {
      data.tenantPrinterId = printerId || undefined;
    }
    if (locationType === 'STORAGE') {
      data.storageLocationId = storageLocationId || undefined;
    }

    await onConfirm(data);
  };

  const currentLocationDisplay = spool
    ? spool.locationType === 'PRINTER'
      ? `Printer: ${spool.tenantPrinter?.instanceName || spool.tenantPrinter?.printer?.name || 'Unknown'}`
      : spool.locationType === 'STORAGE'
        ? `Storage: ${spool.storageLocation?.name || 'Unknown'}`
        : 'No location'
    : '';

  const printerOptions = [
    { value: '', label: 'Choose a printer...' },
    ...printers.map((printer: TenantPrinterWithDetails) => ({ value: printer.id, label: printer.instanceName || printer.printer.name })),
  ];

  const storageLocationSelectOptions = [
    { value: '', label: 'Choose a location...' },
    ...storageLocations.map((loc) => ({ value: loc.id, label: loc.name })),
  ];

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative w-full max-w-md rounded-lg border border-border bg-background p-6 shadow-xl">
                <button
                  onClick={onClose}
                  className="absolute right-4 top-4 rounded p-1 text-foreground-muted hover:bg-background-subtle hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>

                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>
                  <DialogTitle className="text-lg font-semibold text-foreground">
                    Move Spool
                  </DialogTitle>
                </div>

                <p className="text-sm text-foreground-muted mb-4">
                  Current location: <span className="font-medium text-foreground">{currentLocationDisplay}</span>
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Select
                    label="New Location Type"
                    value={locationType}
                    onChange={(e) => {
                      setLocationType(e.target.value as SpoolLocationType);
                      setPrinterId('');
                      setStorageLocationId('');
                    }}
                    options={locationTypeOptions}
                  />

                  {locationType === 'PRINTER' && (
                    <Select
                      label="Select Printer"
                      value={printerId}
                      onChange={(e) => setPrinterId(e.target.value)}
                      options={printerOptions}
                    />
                  )}

                  {locationType === 'STORAGE' && (
                    <Select
                      label="Select Storage Location"
                      value={storageLocationId}
                      onChange={(e) => setStorageLocationId(e.target.value)}
                      options={storageLocationSelectOptions}
                    />
                  )}

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
                      Cancel
                    </Button>
                    <Button type="submit" loading={loading}>
                      Move Spool
                    </Button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
